import * as React from 'react';
import {
    Row, Col, Select, Spin, message, Form,
} from 'antd';
import { InputTypes } from 'components/create-consignment/create-consignment.types';
import GenericField from 'components/create-consignment/single-cn-creation/components/generic-field';
import { ADDRESS_SUB_FIELD_LABELS } from './rate-calculator.constants';
import {
    fetchAddressNodeConfig,
    getNodeListWithAncestralFiltering,
    fetchAddressUsingAddressHierarchy,
} from '../../network/pickup.api';
import { useDebounce } from 'hooks/use-debounce';

const { useState, useEffect } = React;

type LocationKey = 'pincode' | 'city' | 'state' | 'country';

const initialFieldState: Record<string, any> = {
    list: [],
    currentPage: 1,
    hasMore: false,
    loading: false,
    currentSearchQuery: '',
};

const subKeyToNodeType: Partial<Record<string, LocationKey>> = {
    pincode: 'pincode',
    cityName: 'city',
    stateName: 'state',
    countryName: 'country',
};

const nodeTypeToSubKey: Record<LocationKey, string> = {
    pincode: 'pincode',
    city: 'cityName',
    state: 'stateName',
    country: 'countryName',
};

interface SimpleAddressFieldProps {
    field: { key: string; label: string };
    params: any;
    autofillAddressFieldsBasedOnAddressHierarchy?: boolean;
    form?: any;
}

const SimpleAddressField = (props: SimpleAddressFieldProps) => {
    const {
        field, params, autofillAddressFieldsBasedOnAddressHierarchy, form,
    } = props;
    const subFields: string[] = params?.subLayout?.[field.key] || [];

    const [pincodeOptions, setPincodeOptions] = useState({ ...initialFieldState });
    const [cityOptions, setCityOptions] = useState({ ...initialFieldState });
    const [stateOptions, setStateOptions] = useState({ ...initialFieldState });
    const [countryOptions, setCountryOptions] = useState({ ...initialFieldState });
    const [hierarchyData, setHierarchyData] = useState<any>();
    const [isAddressHierarchyPresent, setIsAddressHierarchyPresent] = useState(false);
    const [hierarchyFieldsArr, setHierarchyFieldsArr] = useState<string[]>([]);
    const [parentNodes, setParentNodes] = useState<any>({});
    const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

    const optionsMap = {
        pincode: { get: pincodeOptions, set: setPincodeOptions },
        city: { get: cityOptions, set: setCityOptions },
        state: { get: stateOptions, set: setStateOptions },
        country: { get: countryOptions, set: setCountryOptions },
    };

    const debouncedPincode = useDebounce(fieldValues.pincode);
    const debouncedCity = useDebounce(fieldValues.cityName);
    const debouncedState = useDebounce(fieldValues.stateName);
    const debouncedCountry = useDebounce(fieldValues.countryName);

    const fetchAddressNodeData = async () => {
        const response = await fetchAddressNodeConfig();
        if (response?.data?.hierarchy && Array.isArray(response.data.hierarchy) && response.data.hierarchy.length) {
            setHierarchyData(response.data.hierarchy);
            setHierarchyFieldsArr(response.data.hierarchy.map((d: any) => d.location_key?.toLowerCase()));
            setIsAddressHierarchyPresent(true);
        }
    };

    useEffect(() => {
        if (autofillAddressFieldsBasedOnAddressHierarchy) {
            fetchAddressNodeData();
        }
    }, [autofillAddressFieldsBasedOnAddressHierarchy]);

    const renderDropdownWithLoading = (menu: React.ReactElement, isLoading: boolean) => (
        <>
            {menu}
            {isLoading && (
                <div style={{ padding: '8px', textAlign: 'center' }}>
                    <Spin size="small" />
                    Loading...
                </div>
            )}
        </>
    );

    const handleDropdownScroll = (
        e: React.UIEvent<HTMLDivElement>,
        options: typeof initialFieldState,
        loadMoreFn: (query: string, page: number) => void,
    ) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 10 && options.hasMore && !options.loading) {
            loadMoreFn(options.currentSearchQuery, options.currentPage + 1);
        }
    };

    const loadNodeListData = async (
        nodeType: LocationKey,
        searchQuery: string,
        page: number = 1,
    ) => {
        if (!isAddressHierarchyPresent
            || !autofillAddressFieldsBasedOnAddressHierarchy
            || !hierarchyData?.some((node: any) => node.location_key === nodeType)) return;

        const { set: setOptions } = optionsMap[nodeType];
        const isNewSearch = page === 1;

        setOptions((prev: any) => ({
            ...prev,
            loading: !isNewSearch,
            currentSearchQuery: isNewSearch ? searchQuery : prev.currentSearchQuery,
        }));

        try {
            const response = await getNodeListWithAncestralFiltering({
                nodeType,
                searchQuery,
                parentNodes,
                pageNumber: page,
                resultPerPage: 10,
            });

            if (response?.isSuccess && Array.isArray(response?.data?.data)) {
                const newList = response.data.data.map((item: any) => ({
                    label: `${item.name} (${item.code})`,
                    value: item.code,
                    key: item.code,
                }));
                setOptions((prev: any) => ({
                    ...prev,
                    list: isNewSearch ? newList : [...prev.list, ...newList],
                    currentPage: page,
                    hasMore: response.data?.isNextPresent,
                    loading: false,
                }));
            } else {
                setOptions((prev: any) => ({
                    ...prev,
                    list: isNewSearch ? [] : prev.list,
                    hasMore: false,
                    loading: false,
                }));
                if (response.errorMessage) message.error(response.errorMessage);
            }
        } catch {
            message.error(`Failed to load ${nodeType}s`);
            setOptions((prev: any) => ({
                ...prev,
                loading: false,
                hasMore: false,
                list: isNewSearch ? [] : prev.list,
            }));
        }
    };

    const loadPincodes = (q: string, page: number = 1) => loadNodeListData('pincode', q, page);
    const loadCitiesList = (q: string, page: number = 1) => loadNodeListData('city', q, page);
    const loadStatesList = (q: string, page: number = 1) => loadNodeListData('state', q, page);
    const loadCountriesList = (q: string, page: number = 1) => loadNodeListData('country', q, page);

    const saveAddressUsingAddressHierarchy = async (nodeValue: string, nodeType: LocationKey) => {
        if (!nodeValue) return;
        const fetchLocality = await fetchAddressUsingAddressHierarchy({
            queryString: nodeValue,
            searchType: nodeType,
        });
        if (!fetchLocality.isSuccess) return;

        let nodeFound = false;
        const parentNodesToSet: Record<string, any> = {};
        const newSubFieldValues: Record<string, string | null> = {};

        for (let i = 0; i < (hierarchyData?.length || 0); i += 1) {
            const curNode = hierarchyData[i].location_key?.toLowerCase() as LocationKey;
            if ((nodeFound || curNode === nodeType) && curNode in (fetchLocality?.data || {})) {
                nodeFound = true;
                const value = fetchLocality?.data?.data
                    ? fetchLocality.data.data[curNode]?.value
                    : fetchLocality.data[curNode];
                const nodeId = fetchLocality?.data?.data?.[curNode]?.nodeId ?? null;
                parentNodesToSet[curNode] = nodeId;
                const subKey = nodeTypeToSubKey[curNode];
                if (subKey) newSubFieldValues[subKey] = value || null;
            } else {
                parentNodesToSet[hierarchyData[i].location_key] = undefined;
                const subKey = nodeTypeToSubKey[curNode];
                if (subKey) newSubFieldValues[subKey] = null;
                optionsMap[curNode]?.set((prev: any) => ({ ...prev, ...initialFieldState }));
            }
        }

        setParentNodes((prev: any) => ({ ...prev, ...parentNodesToSet }));
        setFieldValues((prev) => ({ ...prev, ...newSubFieldValues as Record<string, string> }));
        if (form) {
            form.setFieldsValue({ [field.key]: newSubFieldValues });
        }
    };

    const clearDownstreamFields = (nodeType: LocationKey) => {
        let nodeFound = false;
        const newSubFieldValues: Record<string, string | null> = {};
        for (let i = 0; i < (hierarchyData?.length || 0); i += 1) {
            const curNode = hierarchyData[i].location_key as LocationKey;
            if (nodeFound || curNode === nodeType) {
                nodeFound = true;
                const subKey = nodeTypeToSubKey[curNode];
                if (subKey) newSubFieldValues[subKey] = null;
            }
        }
        setFieldValues((prev) => ({ ...prev, ...newSubFieldValues as Record<string, string> }));
        if (form) form.setFieldsValue({ [field.key]: newSubFieldValues });
    };

    useEffect(() => {
        if (debouncedPincode && debouncedPincode.length >= 1) {
            loadPincodes(debouncedPincode);
        }
    }, [debouncedPincode, parentNodes]);

    useEffect(() => {
        if (debouncedCity && debouncedCity.length >= 1) {
            loadCitiesList(debouncedCity);
        }
    }, [debouncedCity, parentNodes]);

    useEffect(() => {
        if (debouncedState && debouncedState.length >= 1) {
            loadStatesList(debouncedState);
        }
    }, [debouncedState, parentNodes]);

    useEffect(() => {
        if (debouncedCountry && debouncedCountry.length >= 1) {
            loadCountriesList(debouncedCountry);
        }
    }, [debouncedCountry, parentNodes]);

    if (!subFields.length) return null;

    const useHierarchy = autofillAddressFieldsBasedOnAddressHierarchy && isAddressHierarchyPresent;

    const renderHierarchyField = (subKey: string, nodeType: LocationKey) => {
        const label = ADDRESS_SUB_FIELD_LABELS[subKey] || subKey;
        const currentOptions = optionsMap[nodeType].get;
        const loaderMap = {
            pincode: loadPincodes,
            city: loadCitiesList,
            state: loadStatesList,
            country: loadCountriesList,
        };
        const loadFn = loaderMap[nodeType];
        const value = fieldValues[subKey];
        const isInHierarchy = hierarchyFieldsArr.includes(nodeType);

        const onChange = (val: string) => {
            setFieldValues((prev) => ({ ...prev, [subKey]: val }));
            if (form) form.setFieldsValue({ [field.key]: { [subKey]: val } });
            if (!val) {
                optionsMap[nodeType].set({ ...initialFieldState });
                clearDownstreamFields(nodeType);
            }
        };

        if (!isInHierarchy) {
            return (
                <GenericField
                    key={subKey}
                    field={{
                        key: subKey,
                        label,
                        type: InputTypes.Input,
                        valuePropName: 'value',
                        placeholder: label,
                    }}
                    params={params}
                    parentObj={field.key}
                    MAX_LIMIT={200}
                />
            );
        }

        return (
            <Col key={subKey} span={24} xs={24} sm={24} md={12}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#111' }}>{label}</div>
                <Form.Item name={[field.key, subKey]} noStyle>
                    <Select
                        value={value}
                        placeholder={label}
                        notFoundContent={currentOptions.loading ? <Spin size="small" /> : (
                            <p style={{ maxHeight: '14px', textAlign: 'center' }}>Type to search</p>
                        )}
                        options={currentOptions.list}
                        onSelect={(val: string) => saveAddressUsingAddressHierarchy(val, nodeType)}
                        onChange={onChange}
                        filterOption={false}
                        onSearch={loadFn}
                        onPopupScroll={(e) => handleDropdownScroll(e, currentOptions, loadFn)}
                        dropdownRender={(menu) => renderDropdownWithLoading(menu, currentOptions.loading)}
                        allowClear
                        showSearch
                        optionLabelProp="value"
                        style={{ width: '100%' }}
                    />
                </Form.Item>
            </Col>
        );
    };

    return (
        <div>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>{field.label}</div>
            <Row gutter={10}>
                {subFields.map((subKey: string) => {
                    const nodeType = subKeyToNodeType[subKey];
                    if (useHierarchy && nodeType) {
                        return (
                            renderHierarchyField(subKey, nodeType)
                        );
                    }
                    return (
                        <GenericField
                            key={subKey}
                            field={{
                                key: subKey,
                                label: ADDRESS_SUB_FIELD_LABELS[subKey] || subKey,
                                type: InputTypes.Input,
                                valuePropName: 'value',
                                placeholder: ADDRESS_SUB_FIELD_LABELS[subKey] || subKey,
                            }}
                            params={params}
                            parentObj={field.key}
                            MAX_LIMIT={200}
                        />
                    );
                })}
            </Row>
        </div>
    );
};

export default SimpleAddressField;
